import type { BadgeProps } from '@radix-ui/themes'
import type { LeakV0 } from '@willie/schemas'
import { colorAtIndex } from '~/components/Charts/tokens'
import type { LeakV0BandKind } from './chartModel'

interface SeriesStyle {
  label: string
  color: string
}

/** One entry per plotted series: the chart, the legend and the tooltip all read their label here. */
export const SERIES = {
  consumption: { label: 'Consumption', color: colorAtIndex(0) },
  willieLeak: { label: 'Willie leak', color: 'var(--blue-9)' },
  shaypLeak: { label: 'Shayp leak', color: colorAtIndex(2) },
  frozenBaseline: { label: 'Frozen baseline', color: 'var(--gray-9)' },
  noiseRibbon: { label: 'Noise band', color: 'var(--gray-a5)' }
} as const satisfies Record<string, SeriesStyle>

/** One accent per open state, so the chart's bands and the episode table's badges cannot diverge. */
const STATE_ACCENT = {
  CANDIDATE: 'yellow',
  CONFIRMED: 'red',
  PENDING_CLEAR: 'blue'
} as const satisfies Record<LeakV0BandKind, BadgeProps['color']>

export const BAND_FILL: Record<LeakV0BandKind, string> = {
  CANDIDATE: `var(--${STATE_ACCENT.CANDIDATE}-a4)`,
  CONFIRMED: `var(--${STATE_ACCENT.CONFIRMED}-a4)`,
  PENDING_CLEAR: `var(--${STATE_ACCENT.PENDING_CLEAR}-a4)`
}

const EPISODE_ACCENT: Partial<Record<LeakV0.LeakEpisodeState, BadgeProps['color']>> = STATE_ACCENT

// `OFF` is a station state that no persisted episode carries, and a closed `CLEAR` one needs no accent.
export const episodeStateAccent = (state: LeakV0.LeakEpisodeState): BadgeProps['color'] =>
  EPISODE_ACCENT[state] ?? 'gray'

export const SOURCE_FILL: Record<LeakV0.Source, string> = {
  backfill: 'var(--gray-8)',
  live: 'var(--jade-9)'
}

export const MARKER_COLOR = {
  transition: 'var(--gray-12)',
  flapContinuation: 'var(--orange-10)',
  paramsHash: 'var(--violet-10)'
} as const

/** The source rail is a DOM strip beside the SVG, so it reproduces the plot area's insets by hand. */
export const Y_AXIS_WIDTH = 56
export const CHART_MARGIN = { top: 8, right: 12, bottom: 0, left: 0 } as const
